import Component from '../core/Component'
import debounce from 'lodash/debounce'
import { substitute } from '../utils/String'
import keycode from 'keycode'

const STATES = {
    OPEN: 'is-open',
    SEARCHING: 'is-searching',
    DISABLED: 'is-disabled'
}

export const EMITS = {
    CHANGE: 'change',
    SELECT: 'select'
}

export default class Autocomplete extends Component {

    constructor(element, options = {}) {
        super(element, {
            searchEngine: 'AjaxSearch',
            searchEngineOptions: {
                url: null
            },
            ...options
        })

        this.ref = {
            input: null,
            dropdown: null,
            itemTemplate: null,
        }

        this.results = []
        this.highlightedItemEl = null
        this.selectedItem = null
        this.lastValue = null

        this.isFocus = false
        this.isDisabled = null
        this.cache = {}

        this.debounceSearchSuggestions = debounce(this.searchSuggestions, 500)
    }

    prepare() {
        this.checkInputAvailability()
    }

    attachListeners() {
        this.ref.input.addEventListener('change', this.handleChange)
        this.ref.input.addEventListener('input', this.handleChange)
        this.ref.input.addEventListener('blur', this.handleInputBlur)
        this.ref.input.addEventListener('focus', this.handleInputFocus)
        this.ref.input.addEventListener('keyup', this.handleKeyUp)
        this.ref.input.addEventListener('keydown', this.handleKeyDown)
        this.ref.dropdown.addEventListener('click', this.handleDropdownClick)
        this.element.addEventListener('mousedown', this.handleMouseDown)
        this.element.addEventListener('pointerdown', this.handleMouseDown)
        this.element.addEventListener('mouseup', this.handleMouseUp)
        this.element.addEventListener('pointerup', this.handleMouseUp)
    }

    detachListeners() {
        this.ref.input.removeEventListener('change', this.handleChange)
        this.ref.input.removeEventListener('input', this.handleChange)
        this.ref.input.removeEventListener('blur', this.handleInputBlur)
        this.ref.input.removeEventListener('focus', this.handleInputFocus)
        this.ref.input.removeEventListener('keyup', this.handleKeyUp)
        this.ref.input.removeEventListener('keydown', this.handleKeyDown)
        this.ref.dropdown.removeEventListener('click', this.handleDropdownClick)
        this.element.removeEventListener('mousedown', this.handleMouseDown)
        this.element.removeEventListener('mouseup', this.handleMouseUp)
        this.element.removeEventListener('pointerdown', this.handleMouseDown)
        this.element.removeEventListener('pointerup', this.handleMouseUp)

    }

    handleChange = event => {
        const value = event.target.value
        if (this.lastValue === value) {
            return
        }

        this.lastValue = value
        this.emit(EMITS.CHANGE)
        this.debounceSearchSuggestions(value)
    }

    handleMouseDown = () => {
        this.isMouseDown = true
    }

    handleMouseUp = () => {
        this.isMouseDown = false
    }

    handleKeyDown = event => {
        switch (keycode(event.keyCode)) {
            case 'enter':
                if (this.is(STATES.SEARCHING)) {
                    event.preventDefault()
                }
                if (this.highlightedItemEl && this.is(STATES.OPEN)) {
                    event.preventDefault()
                    this.selectItem(this.highlightedItemEl)
                }
                break
            case 'down':
            case 'up':
                event.preventDefault()
                break
            default:
                this.deselectItem()
                break
        }
    }

    handleKeyUp = event => {
        switch (keycode(event.keyCode)) {
            case 'esc':
                this.hide()
                break
            case 'enter':
            case 'left':
            case 'right':
                break
            case 'down':
                this.show()
                this.highlightNext()
                break
            case 'up':
                this.show()
                this.highlightPrevious()
                break
            default:
                break
        }
    }

    handleDropdownClick = event => {
        const element = event.target.closest('[data-item]')
        if (!element) {
            return
        }
        this.selectItem(element)
    }

    applyResults(results) {
        this.setResults(results)
        if (results.length) {
            this.show()
        } else {
            this.hide()
        }
    }

    setResults(results) {
        this.results = results
        const itemTemplate = this.ref.itemTemplate.innerHTML.trim()

        this.ref.dropdown.innerHTML = results
            .slice(0, 10)
            .map(item => substitute(itemTemplate, item))
            .join('')
    }

    handleInputBlur = () => {
        this.isFocus = false
        this.hide()
    }

    handleInputFocus = () => {
        this.getSearchEngine()

        this.isFocus = true
        this.show()
    }

    async searchSuggestions(value) {
        if (!value) {
            return
        }

        const searchEngine = await this.getSearchEngine()
        if (!searchEngine) {
            return
        }

        searchEngine.abort()
        if (this.cache[value]) {
            this.applyResults(this.cache[value])
        } else {
            const results = await searchEngine.searchSuggestions(value)
            this.cache[value] = results
            this.results = results
            this.applyResults(results)
        }
    }

    async selectItem(element) {
        if (!element) {
            return
        }

        const item = element ? JSON.parse(element.dataset.item) : null
        if (!item) {
            return
        }

        this.lastValue = item.title
        this.ref.input.value = item.title
        this.selectedItem = item
        this.hide()
        this.emit(EMITS.SELECT)
    }

    deselectItem() {
        if (!this.selectedItem || (this.selectedItem && this.selectedItem.title === this.ref.input.value)) {
            return
        }
        this.selectedItem = null
    }

    async getValue() {
        let status = 'fail'
        let value = null

        //prazdna hodnota je validni, proto se bere jako vybrana
        if (!this.ref.input.value) {
            status = 'ok'
        }

        //je hodnota vybrana z nabizenych? vratit
        if (this.selectedItem && status !== 'ok') {
            status = 'ok'
            value = this.selectedItem
        }

        //je submitnuto s hodnoutou, ktera nebyla vybrana z nabizenych moznosti? pouzije se searchengine, at najde nejblizsi vhodnou hodnotu
        if (!this.selectedItem && status !== 'ok') {
            try {
                const searchEngine = await this.getSearchEngine()
                const result = await searchEngine.searchQuery(this.ref.input.value)
                if (result) {
                    value = {
                        ...value,
                        ...result
                    }
                }
            } catch (error) {
                console.error(error)
            }
        }

        //je searchengine googlemaps a nejsou znamy souradnice? pak je potreba dotahnout lat/lng a tu na KMBETA i OKRES!
        if (this.options.searchEngine === 'GoogleMapsSearch' && ((value && value.id && value.lat === undefined && value.lng === undefined) || value?.place?.address_components === undefined)) {
            try {
                const searchEngine = await this.getSearchEngine()
                const result = await searchEngine.fetchPlace(value.id)
                if (result) {
                    value = {
                        ...value,
                        ...result
                    }
                }
            } catch (error) {
                console.error(error)
            }
        }

        return value
    }

    show() {
        if (this.results.length == 0 || !this.isFocus) {
            return
        }

        this.element.classList.add(STATES.OPEN)
    }

    hide() {
        setTimeout(() => {
            if (this.isMouseDown) {
                return
            }
            this.element.classList.remove(STATES.OPEN)
        }, 100)
    }

    highlightItem(item) {
        this.getItemEls().forEach(itemEl => itemEl.classList.remove('is-active'))
        item?.classList.add('is-active')
        this.highlightedItemEl = item
    }

    highlightPrevious() {
        const itemEls = this.getItemEls()
        if (!itemEls || !this.results) {
            return
        }

        let index = itemEls.findIndex(itemEl => itemEl === this.highlightedItemEl) || 0
        index = index === 0 && itemEls.length - 1 || index - 1

        this.highlightItem(itemEls[index])
    }

    highlightNext() {
        const itemEls = this.getItemEls()
        if (!itemEls) {
            return
        }

        let index = itemEls.findIndex(itemEl => itemEl === this.highlightedItemEl) || 0
        index = (index + 1) % itemEls.length

        this.highlightItem(itemEls[index])
    }

    getItemEls() {
        return [...this.ref.dropdown.querySelectorAll('[data-item]')]
    }

    async getSearchEngine() {
        let searchEngine = this._searchEngine

        if (searchEngine) {
            return searchEngine
        }

        try {
            this.element.classList.add(STATES.SEARCHING)
            const module = await import(`../services/SearchEngine/${this.options.searchEngine}`)

            searchEngine = new module.default(this.options.searchEngineOptions)
            searchEngine.onSearchStart = () => this.element.classList.add(STATES.SEARCHING)
            searchEngine.onSearchEnd = () => this.element.classList.remove(STATES.SEARCHING)
            this._searchEngine = searchEngine
        } catch (err) {
            console.error(err)
            console.error(`Failed to import "${this.options.searchEngine}" search engine.`)
        } finally {
            this.element.classList.remove(STATES.SEARCHING)
        }

        return searchEngine
    }

    checkInputAvailability() {
        if (!this.ref.input.disabled && typeof this.ref.input.getAttribute('readonly') !== 'undefined') {
            this.enable()
        } else {
            this.disable()
        }
    }

    disable() {
        if (this.isDisabled === true) {
            return
        }
        this.detachListeners()
        this.element.classList.add(STATES.DISABLED)
        this.ref.input.setAttribute('readonly', '')
        this.isDisabled = true
        if (this._searchEngine) {
            this._searchEngine.abort()
        }
    }

    enable() {
        if (this.isDisabled === false) {
            return
        }

        console.error('enable')
        this.attachListeners()
        this.element.classList.remove(STATES.DISABLED)
        this.ref.input.removeAttribute('readonly')
        this.isDisabled = false
    }
}